import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { articles } from '../../mocks/articles';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-navy-900 mb-4">Makale Bulunamadı</h1>
          <Link to="/legal-insights" className="text-gold-500 hover:text-gold-600 cursor-pointer">
            Hukuki Görüşlere Dön
          </Link>
        </div>
      </div>
    );
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/80"></div>
        </div>
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/legal-insights"
              className="inline-flex items-center text-gold-500 hover:text-gold-400 mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-sans text-sm">Hukuki Görüşlere Dön</span>
            </Link>
            <div className="flex items-center space-x-4 mb-6">
              <span className="px-4 py-2 bg-gold-500 text-white text-xs font-sans font-semibold uppercase tracking-wider rounded-full">
                {article.category}
              </span>
              <span className="text-sm text-white/80">{article.date}</span>
              <span className="text-sm text-white/80">• {article.readTime} okuma</span>
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl text-white mb-6 leading-tight">{article.title}</h1>
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center">
                <span className="text-lg font-sans font-bold text-white">
                  {article.author.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <div className="font-sans text-base font-medium text-white">{article.author}</div>
                <div className="font-sans text-sm text-white/70">Kıdemli Avukat</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 bg-white">
        <div className="max-w-[800px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <div className="prose prose-lg max-w-none">
              <p className="font-sans text-xl text-gray-700 leading-relaxed mb-8 font-medium">
                {article.excerpt}
              </p>
              <div className="font-sans text-base text-gray-700 leading-relaxed space-y-6">
                <p>{article.content}</p>
                <p>
                  Bu makalede, konuyla ilgili temel hukuki çerçeveyi, uygulamadaki zorlukları ve çözüm önerilerini detaylı olarak ele alacağız. Müvekkillerimizin ve hukuk camiasının bu önemli konuda bilgilendirilmesi amacıyla hazırlanmış bu kapsamlı rehber, pratik örnekler ve güncel içtihatlarla desteklenmiştir.
                </p>
                <h2 className="font-serif text-2xl text-navy-900 mt-8 mb-4">Hukuki Çerçeve</h2>
                <p>
                  Türk hukuk sisteminde bu konuya ilişkin düzenlemeler, hem ulusal mevzuat hem de uluslararası anlaşmalar çerçevesinde şekillenmiştir. İlgili kanun hükümleri, yargı kararları ve doktrindeki görüşler ışığında konuyu değerlendirmek gerekmektedir.
                </p>
                <h2 className="font-serif text-2xl text-navy-900 mt-8 mb-4">Pratik Uygulamalar</h2>
                <p>
                  Uygulamada karşılaşılan sorunlar ve bu sorunlara yönelik çözüm önerileri, müvekkillerimizin hukuki süreçleri daha iyi anlamalarına ve doğru kararlar almalarına yardımcı olmaktadır. Deneyimlerimiz ışığında, bu alanda dikkat edilmesi gereken kritik noktaları vurgulamak önemlidir.
                </p>
                <h2 className="font-serif text-2xl text-navy-900 mt-8 mb-4">Sonuç ve Öneriler</h2>
                <p>
                  Sonuç olarak, bu konuda başarılı olmak için kapsamlı bir hukuki strateji geliştirmek ve uzman hukuki danışmanlık almak kritik öneme sahiptir. Hukuk büromuz, bu alanda geniş deneyime sahip avukatlarıyla müvekkillerimize en yüksek kalitede hizmet sunmaya devam etmektedir.
                </p>
              </div>
            </div>

            <div className="mt-12 p-8 bg-gray-50 rounded-lg border-l-4 border-gold-500">
              <p className="font-sans text-sm text-gray-700 leading-relaxed">
                <strong className="text-navy-900">Yasal Uyarı:</strong> Bu makale genel bilgilendirme amaçlıdır ve hukuki tavsiye niteliği taşımamaktadır. Özel durumunuz için mutlaka uzman bir avukata danışmanız önerilir.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Author Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[800px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="bg-white p-8 rounded-lg shadow-md">
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 bg-gold-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-sans font-bold text-white">
                  {article.author.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-sans text-xl font-semibold text-navy-900 mb-2">{article.author}</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">
                  {article.category} alanında uzman avukat. 15+ yıllık deneyimi ile müvekkillerine stratejik hukuki danışmanlık sunmaktadır.
                </p>
                <Link
                  to="/attorneys"
                  className="inline-flex items-center text-gold-500 hover:text-gold-600 text-sm font-medium cursor-pointer"
                >
                  <span>Profili Görüntüle</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="font-serif text-4xl text-navy-900 mb-4">İlgili Makaleler</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/legal-insights/${relatedArticle.slug}`}
                  className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedArticle.image}
                      alt={relatedArticle.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="px-3 py-1 bg-gold-500/10 text-gold-500 text-xs font-sans font-medium rounded-full">
                      {relatedArticle.category}
                    </span>
                    <h3 className="font-sans text-lg font-semibold text-navy-900 mt-3 mb-2 line-clamp-2 group-hover:text-gold-500 transition-colors">
                      {relatedArticle.title}
                    </h3>
                    <p className="font-sans text-sm text-gray-600 line-clamp-2">{relatedArticle.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;